import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Swords, ChevronRight, Trophy, Flame, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompeteTestPage() {
  const { accessCode } = useParams();
  const [competitionData, setCompetitionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Test state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Streak tracking
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  useEffect(() => {
    fetchCompetition();
  }, [accessCode]);

  const fetchCompetition = async () => {
    try {
      const response = await axios.get(`${API}/competitions/by-code/${accessCode}`);
      setCompetitionData(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        setError("You have already completed this competition");
      } else {
        setError("Competition not found or invalid link");
      }
    } finally {
      setLoading(false);
    }
  };

  const competition = competitionData?.competition;
  const test = competitionData?.test;
  const cocktails = competitionData?.cocktails || [];
  const playerNum = competitionData?.player_num;
  const playerName = competitionData?.player_name;
  const currentCocktail = cocktails[currentQuestion];
  const testType = test?.test_type || "full";

  const generateOptions = (cocktail, field, allCocktails) => {
    const correctValue = cocktail[field] || "None";
    let options = new Set([correctValue]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c[field]) {
        options.add(c[field]);
      }
    });
    
    const genericOptions = {
      garnish: ["Lime wheel", "Orange peel", "Cherry", "Mint sprig", "None", "Olive", "Lemon twist"],
      glassware: ["Rocks", "Coupe", "Highball", "Martini", "Collins", "Nick & Nora", "Flute"],
      side_elements: ["None", "N/A", "Espresso shot", "Bitters on the side", "Sugar rim", "Salt rim"]
    };
    
    if (genericOptions[field]) {
      genericOptions[field].forEach(opt => options.add(opt));
    }
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const generateIngredientOptions = (cocktail, allCocktails) => {
    const correctIngredients = cocktail.ingredients?.map(i => 
      `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
    ).join(", ") || "None";
    
    let options = new Set([correctIngredients]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c.ingredients?.length > 0) {
        const ing = c.ingredients.map(i => 
          `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
        ).join(", ");
        options.add(ing);
      }
    });
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const startTest = () => {
    setTestStarted(true);
  };

  const handleAnswer = (field, value) => {
    setAnswers(prev => ({
      ...prev,
      [currentCocktail.id]: {
        ...prev[currentCocktail.id],
        [field]: value
      }
    }));
  };

  const getQuestionsForTestType = () => {
    switch (testType) {
      case "ingredients":
        return ["ingredients"];
      case "presentation":
        return ["garnish", "glassware"];
      case "speed":
      case "full":
      default:
        return ["ingredients", "garnish", "glassware", "side_elements"];
    }
  };

  const questions = getQuestionsForTestType();

  const checkAndUpdateStreak = () => {
    const userAnswers = answers[currentCocktail?.id] || {};
    const correctIngredients = currentCocktail?.ingredients?.map(i => 
      `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
    ).join(", ") || "None";
    
    let allCorrect = true;
    if (questions.includes("ingredients") && userAnswers.ingredients !== correctIngredients) allCorrect = false;
    if (questions.includes("garnish") && userAnswers.garnish !== (currentCocktail?.garnish || "None")) allCorrect = false;
    if (questions.includes("glassware") && userAnswers.glassware !== currentCocktail?.glassware) allCorrect = false;
    if (questions.includes("side_elements") && userAnswers.side_elements !== (currentCocktail?.side_elements || "None" || "N/A")) allCorrect = false;
    
    if (allCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
      }
    } else {
      setCurrentStreak(0);
    }
  };

  const nextQuestion = () => {
    checkAndUpdateStreak();
    if (currentQuestion < cocktails.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const calculateResults = () => {
    const questionResults = cocktails.map(cocktail => {
      const userAnswers = answers[cocktail.id] || {};
      const correctIngredients = cocktail.ingredients?.map(i => 
        `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
      ).join(", ") || "None";
      
      const ingredientsCorrect = questions.includes("ingredients") 
        ? userAnswers.ingredients === correctIngredients 
        : true;
      const garnishCorrect = questions.includes("garnish") 
        ? userAnswers.garnish === (cocktail.garnish || "None")
        : true;
      const glasswareCorrect = questions.includes("glassware")
        ? userAnswers.glassware === cocktail.glassware
        : true;
      const sideCorrect = questions.includes("side_elements")
        ? userAnswers.side_elements === (cocktail.side_elements || "None" || "N/A")
        : true;
      
      const score = [
        questions.includes("ingredients") && ingredientsCorrect,
        questions.includes("garnish") && garnishCorrect,
        questions.includes("glassware") && glasswareCorrect,
        questions.includes("side_elements") && sideCorrect
      ].filter(Boolean).length;
      
      return {
        cocktail_id: cocktail.id,
        cocktail_name: cocktail.name,
        ingredients_correct: ingredientsCorrect,
        garnish_correct: garnishCorrect,
        glassware_correct: glasswareCorrect,
        side_elements_correct: sideCorrect,
        score
      };
    });

    const totalScore = questionResults.reduce((sum, r) => sum + r.score, 0);
    const totalPossible = cocktails.length * questions.length;
    const percentage = totalPossible > 0 ? (totalScore / totalPossible) * 100 : 0;

    return { answers: questionResults, totalScore, totalPossible, percentage };
  };

  const submitTest = async () => {
    checkAndUpdateStreak();
    const resultsData = calculateResults();
    setSubmitting(true);
    
    try {
      // Create test result
      const resultResponse = await axios.post(`${API}/test-results`, {
        saved_test_id: test.id,
        staff_id: competition[`player${playerNum}_id`],
        staff_name: playerName,
        answers: resultsData.answers,
        total_score: resultsData.totalScore,
        total_possible: resultsData.totalPossible,
        percentage: resultsData.percentage,
        streak: currentStreak,
        best_streak: Math.max(bestStreak, currentStreak)
      });
      
      // Update competition
      await axios.put(`${API}/competitions/${competition.id}/submit?player_num=${playerNum}&result_id=${resultResponse.data.id}&score=${resultsData.percentage}`);
      
      setResults(resultsData);
      setTestCompleted(true);
      toast.success("Result submitted!");
    } catch (error) {
      toast.error("Failed to submit result");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-[hsl(var(--muted-foreground))]">Loading competition...</div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Competition</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Results Screen
  if (testCompleted && results) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <Trophy size={64} className={`mx-auto mb-4 ${
                results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
              }`} />
              <h1 className="text-3xl font-bold mb-2">Competition Complete!</h1>
              <p className="text-[hsl(var(--muted-foreground))] mb-6">{playerName}</p>
              
              <div className={`inline-block px-8 py-4 rounded-xl border-2 ${
                results.percentage >= 80 
                  ? 'border-green-500 bg-green-500/10' 
                  : results.percentage >= 60 
                  ? 'border-[#C36A2F] bg-[#C36A2F]/10'
                  : 'border-red-500 bg-red-500/10'
              }`}>
                <p className={`text-5xl font-bold ${
                  results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
                }`}>
                  {results.percentage.toFixed(0)}%
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {results.totalScore} / {results.totalPossible} points
                </p>
              </div>

              {bestStreak > 0 && (
                <div className="mt-4 flex items-center justify-center gap-2 text-orange-500">
                  <Flame size={20} />
                  <span className="font-bold">Best Streak: {bestStreak}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] text-center">
            <p className="text-[hsl(var(--muted-foreground))]">
              Waiting for your opponent to complete their test...
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              Results will be available once both players finish.
            </p>
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Start Screen
  if (!testStarted) {
    const opponentName = playerNum === 1 ? competition?.player2_name : competition?.player1_name;
    
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Swords size={48} className="mx-auto text-red-500 mb-4" />
              <h1 className="text-2xl font-bold mb-2">{competition?.name}</h1>
              
              <div className="mt-6 flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className={`w-14 h-14 rounded-full ${playerNum === 1 ? 'bg-blue-500/20' : 'bg-red-500/20'} flex items-center justify-center mx-auto mb-2`}>
                    <span className={`text-xl font-bold ${playerNum === 1 ? 'text-blue-400' : 'text-red-400'}`}>
                      {playerName?.charAt(0)}
                    </span>
                  </div>
                  <p className="font-medium text-[hsl(var(--primary))]">{playerName}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">(You)</p>
                </div>
                
                <Swords className="text-[hsl(var(--muted-foreground))]" size={24} />
                
                <div className="text-center">
                  <div className={`w-14 h-14 rounded-full ${playerNum === 2 ? 'bg-blue-500/20' : 'bg-red-500/20'} flex items-center justify-center mx-auto mb-2`}>
                    <span className={`text-xl font-bold ${playerNum === 2 ? 'text-blue-400' : 'text-red-400'}`}>
                      {opponentName?.charAt(0)}
                    </span>
                  </div>
                  <p className="font-medium">{opponentName}</p>
                </div>
              </div>
              
              <div className="mt-6 space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                <p>{cocktails.length} cocktails • {test?.name}</p>
                <p>Highest score wins!</p>
              </div>
            </div>

            <Button
              onClick={startTest}
              className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            >
              Start Competition
              <ChevronRight size={18} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Quiz Screen (same as regular test but with streak display)
  const currentAnswers = answers[currentCocktail?.id] || {};
  const ingredientOptions = generateIngredientOptions(currentCocktail, cocktails);
  const garnishOptions = generateOptions(currentCocktail, 'garnish', cocktails);
  const glasswareOptions = generateOptions(currentCocktail, 'glassware', cocktails);
  const sideOptions = generateOptions(currentCocktail, 'side_elements', cocktails);

  const answeredQuestions = questions.filter(q => currentAnswers[q]).length;
  const isQuestionComplete = answeredQuestions === questions.length;
  const isLastQuestion = currentQuestion === cocktails.length - 1;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Progress & Streak */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Question {currentQuestion + 1} of {cocktails.length}
          </p>
          {currentStreak > 0 && (
            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/20 text-orange-400">
              <Flame size={16} />
              <span className="font-bold">{currentStreak}</span>
            </div>
          )}
          <div className="flex gap-1">
            {cocktails.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full ${
                  idx === currentQuestion 
                    ? 'bg-[hsl(var(--primary))]' 
                    : idx < currentQuestion 
                    ? 'bg-green-500' 
                    : 'bg-[hsl(var(--muted))]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Cocktail Name */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-6 text-center">
            <h2 className="text-3xl font-bold text-[hsl(var(--primary))]">{currentCocktail?.name}</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{currentCocktail?.category} • {currentCocktail?.base_spirit}</p>
          </CardContent>
        </Card>

        {/* Questions */}
        <div className="space-y-6">
          {questions.includes("ingredients") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What are the ingredients?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.ingredients || ""} 
                  onValueChange={(val) => handleAnswer('ingredients', val)}
                >
                  {ingredientOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-start space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.ingredients === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`ing-${idx}`} />
                      <Label htmlFor={`ing-${idx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("garnish") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What is the garnish?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.garnish || ""} 
                  onValueChange={(val) => handleAnswer('garnish', val)}
                >
                  {garnishOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.garnish === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`gar-${idx}`} />
                      <Label htmlFor={`gar-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("glassware") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What glassware is used?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.glassware || ""} 
                  onValueChange={(val) => handleAnswer('glassware', val)}
                >
                  {glasswareOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.glassware === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`glass-${idx}`} />
                      <Label htmlFor={`glass-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("side_elements") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Any side elements?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.side_elements || ""} 
                  onValueChange={(val) => handleAnswer('side_elements', val)}
                >
                  {sideOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.side_elements === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`side-${idx}`} />
                      <Label htmlFor={`side-${idx}`} className="cursor-pointer">{opt || "None"}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <div className="flex-1" />
          {isLastQuestion ? (
            <Button
              onClick={submitTest}
              disabled={!isQuestionComplete || submitting}
              className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            >
              {submitting ? "Submitting..." : "Finish Competition"}
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              disabled={!isQuestionComplete}
              className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            >
              Next
              <ChevronRight size={18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
